import React, { useState, useEffect } from 'react';

const HubSpotLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [creatingUsers, setCreatingUsers] = useState(false);

  const BASE_URL = 'https://its3dze2da.execute-api.us-west-2.amazonaws.com/prod';

  // Helper function to extract value from HubSpot property objects
  const getPropertyValue = (property) => {
    if (!property) return null;
    if (typeof property === 'string') return property;
    if (typeof property === 'object' && property.value !== undefined) {
      return property.value;
    }
    return property;
  };

  const fetchLists = async (retryCount = 3) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log('Fetching from:', `${BASE_URL}/hubspot?action=getAllLists`);
      const response = await fetch(`${BASE_URL}/hubspot?action=getAllLists`);
      
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received data:', data);
      console.log('Data type:', typeof data);
      console.log('Data keys:', Object.keys(data));
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Handle different possible response structures
      let extractedLists = [];
      if (Array.isArray(data)) {
        // If data is directly an array
        extractedLists = data;
      } else if (data.lists && Array.isArray(data.lists)) {
        // If data has a lists property
        extractedLists = data.lists;
      } else if (data.body) {
        // If data has a body property (Lambda response)
        try {
          const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
          if (Array.isArray(bodyData)) {
            extractedLists = bodyData;
          } else if (bodyData.lists && Array.isArray(bodyData.lists)) {
            extractedLists = bodyData.lists;
          }
        } catch (e) {
          console.error('Error parsing body:', e);
        }
      }

      console.log('Extracted lists:', extractedLists);
      
      setLists(extractedLists);
      setSuccess(`Successfully loaded ${extractedLists.length} lists`);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching lists:', error);
      
      if (retryCount > 0) {
        console.log(`Retrying... ${retryCount} attempts left`);
        setTimeout(() => fetchLists(retryCount - 1), 2000);
      } else {
        setError(`Failed to get lists after multiple attempts. Please try again later.\n\n\nError: ${error.message}`);
        setLoading(false);
      }
    }
  };

  const fetchContactsByList = async (listId, retryCount = 3) => {
    try {
      setContactsLoading(true);
      setContactsError(null);

      console.log('Fetching contacts for list:', listId);
      const response = await fetch(`${BASE_URL}/hubspot?action=fetchContactsByList&listId=${listId}`);
      
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received contacts data:', data);
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Handle different possible response structures
      let extractedContacts = [];
      if (Array.isArray(data)) {
        extractedContacts = data;
      } else if (data.body) {
        try {
          const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
          if (Array.isArray(bodyData)) {
            extractedContacts = bodyData;
          }
        } catch (e) {
          console.error('Error parsing body:', e);
        }
      }

      console.log('Extracted contacts:', extractedContacts);
      setContacts(extractedContacts);
      setContactsLoading(false);

    } catch (error) {
      console.error('Error fetching contacts:', error);
      
      if (retryCount > 0) {
        console.log(`Retrying contacts fetch... ${retryCount} attempts left`);
        setTimeout(() => fetchContactsByList(listId, retryCount - 1), 2000);
      } else {
        setContactsError(`Failed to get contacts after multiple attempts. Error: ${error.message}`);
        setContactsLoading(false);
      }
    }
  };

  const handleListClick = (list) => {
    console.log('Clicked list:', list);
    setSelectedList(list);
    setModalOpen(true);
    setContacts([]);
    setContactsError(null);
    
    // Fetch contacts for this list
    if (list.listId) {
      fetchContactsByList(list.listId);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedList(null);
    setContacts([]);
    setContactsError(null);
    setShowActionsDropdown(false);
  };

  const createUsersFromContacts = async () => {
    if (!contacts || contacts.length === 0) {
      alert('No contacts to create users from');
      return;
    }

    setCreatingUsers(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      for (const contact of contacts) {
        try {
          // Extract contact data
          const studentId = getPropertyValue(contact.properties?.hs_object_id);
          const studentFirstName = getPropertyValue(contact.properties?.student_first_name);
          const studentLastName = getPropertyValue(contact.properties?.student_last_name);
          const grade = getPropertyValue(contact.properties?.grade_applying_for);
          const parentFirstName = getPropertyValue(contact.properties?.firstname);
          const parentLastName = getPropertyValue(contact.properties?.lastname);
          const parentEmail = getPropertyValue(contact.properties?.email);
          const parentPhone = getPropertyValue(contact.properties?.phone);

          // Skip if essential data is missing
          if (!studentFirstName || !studentLastName || !parentEmail) {
            errors.push(`Skipped contact: Missing essential data (Student: ${studentFirstName} ${studentLastName}, Parent Email: ${parentEmail})`);
            errorCount++;
            continue;
          }

          // Create student user (no email, no password)
          const studentUser = {
            email: null, // Students don't have emails
            first_name: studentFirstName,
            last_name: studentLastName,
            dob: null, // We don't have this from HubSpot
            avatar: null,
            hubspot_id: studentId, // Store HubSpot ID for reference
            grade: grade
          };

          // Create parent user (with email, no password)
          const parentUser = {
            email: parentEmail,
            first_name: parentFirstName || 'Parent',
            last_name: parentLastName || 'Guardian',
            dob: null,
            avatar: null,
            phone: parentPhone
          };

          // Create users via API (we'll need to create these endpoints)
          const studentResponse = await fetch('/api/users/create-without-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(studentUser)
          });

          const parentResponse = await fetch('/api/users/create-without-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(parentUser)
          });

          if (!studentResponse.ok || !parentResponse.ok) {
            throw new Error(`Failed to create users for ${studentFirstName} ${studentLastName}`);
          }

          const studentData = await studentResponse.json();
          const parentData = await parentResponse.json();

          // Create relationship between parent and student
          const relationship = {
            parent_table: 'users',
            parent_id: parentData.user.id,
            child_table: 'users',
            child_id: studentData.user.id,
            relationship_type: 'parent_child'
          };

          const relationshipResponse = await fetch('/api/relationships', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(relationship)
          });

          if (!relationshipResponse.ok) {
            throw new Error(`Failed to create relationship for ${studentFirstName} ${studentLastName}`);
          }

          successCount++;

        } catch (error) {
          console.error('Error creating users for contact:', error);
          errors.push(`Error for ${getPropertyValue(contact.properties?.student_first_name)} ${getPropertyValue(contact.properties?.student_last_name)}: ${error.message}`);
          errorCount++;
        }
      }

      // Show results
      const message = `Users created successfully!\n\nSuccess: ${successCount}\nErrors: ${errorCount}\n\n${errors.length > 0 ? 'Errors:\n' + errors.slice(0, 5).join('\n') + (errors.length > 5 ? '\n...' : '') : ''}`;
      alert(message);

    } catch (error) {
      console.error('Error in createUsersFromContacts:', error);
      alert(`Error creating users: ${error.message}`);
    } finally {
      setCreatingUsers(false);
      setShowActionsDropdown(false);
    }
  };

  const handleRefresh = () => {
    fetchLists();
  };

  useEffect(() => {
    fetchLists();
  }, []);

  const totalContacts = lists.reduce((sum, list) => {
    return sum + (list.metaData ? list.metaData.size : 0);
  }, 0);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>HubSpot Lists Viewer</h1>
        <p style={styles.subtitle}>View and manage your HubSpot contact lists</p>
      </div>

      {loading && (
        <div style={styles.loading}>
          <p>Loading lists...</p>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div style={styles.success}>
          <h3>Success</h3>
          <p>{success}</p>
        </div>
      )}

      {!loading && !error && (
        <div style={styles.listsContainer}>
          <div style={styles.stats}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>{lists.length}</div>
              <div style={styles.statLabel}>Total Lists</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>{totalContacts}</div>
              <div style={styles.statLabel}>Total Contacts</div>
            </div>
          </div>

          <div style={styles.listsGrid}>
            {lists.length === 0 ? (
              <p style={styles.noLists}>No lists found</p>
            ) : (
              lists.map((list, index) => (
                <div
                  key={list.listId || index}
                  style={styles.listCard}
                  onClick={() => handleListClick(list)}
                >
                  <h3 style={styles.listTitle}>{list.name || 'Unnamed List'}</h3>
                  <p style={styles.listInfo}>
                    <strong>List ID:</strong> <span style={styles.listId}>{list.listId || 'N/A'}</span>
                  </p>
                  <p style={styles.listInfo}>
                    <strong>Created:</strong> {list.createdAt ? new Date(list.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                  <p style={styles.listInfo}>
                    <strong>Updated:</strong> {list.updatedAt ? new Date(list.updatedAt).toLocaleDateString() : 'Unknown'}
                  </p>
                  <p style={styles.listInfo}>
                    <strong>Contacts:</strong> {list.metaData ? list.metaData.size : 'Unknown'}
                  </p>
                  <p style={styles.listInfo}>
                    <strong>Dynamic:</strong> {list.dynamic ? 'Yes' : 'No'}
                  </p>
                </div>
              ))
            )}
          </div>

          <button style={styles.refreshBtn} onClick={handleRefresh}>
            Refresh Lists
          </button>
        </div>
      )}

      {/* Modal for displaying contacts */}
      {modalOpen && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Contacts in "{selectedList?.name || 'Unknown List'}"
              </h2>
              <div style={styles.modalActions}>
                <div style={styles.dropdownContainer}>
                  <button 
                    style={styles.actionsButton} 
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    disabled={creatingUsers}
                  >
                    Actions {creatingUsers ? '(Creating...)' : '▼'}
                  </button>
                  {showActionsDropdown && (
                    <div style={styles.dropdownMenu}>
                      <button 
                        style={styles.dropdownItem}
                        onClick={createUsersFromContacts}
                        disabled={creatingUsers}
                      >
                        Create Users
                      </button>
                    </div>
                  )}
                </div>
                <button style={styles.closeButton} onClick={closeModal}>
                  ×
                </button>
              </div>
            </div>
            
            <div style={styles.modalBody}>
              {contactsLoading && (
                <div style={styles.loading}>
                  <p>Loading contacts...</p>
                </div>
              )}
              
              {contactsError && (
                <div style={styles.error}>
                  <h3>Error</h3>
                  <p>{contactsError}</p>
                </div>
              )}
              
              {!contactsLoading && !contactsError && (
                <div>
                  <div style={styles.contactsStats}>
                    <p><strong>Total Contacts:</strong> {contacts.length}</p>
                  </div>
                  
                  {contacts.length === 0 ? (
                    <p style={styles.noContacts}>No contacts found in this list</p>
                  ) : (
                    <div style={styles.contactsList}>
                      {contacts.map((contact, index) => (
                        <div key={contact.vid || index} style={styles.contactCard}>
                          <h4 style={styles.contactName}>
                            {getPropertyValue(contact.properties?.firstname || contact.properties?.student_first_name) || 'Unknown'} {' '}
                            {getPropertyValue(contact.properties?.lastname || contact.properties?.student_last_name) || ''}
                          </h4>
                          <div style={styles.contactDetails}>
                            {getPropertyValue(contact.properties?.email) && (
                              <p><strong>Email:</strong> {getPropertyValue(contact.properties.email)}</p>
                            )}
                            {getPropertyValue(contact.properties?.phone) && (
                              <p><strong>Phone:</strong> {getPropertyValue(contact.properties.phone)}</p>
                            )}
                            {getPropertyValue(contact.properties?.grade_applying_for) && (
                              <p><strong>Grade:</strong> {getPropertyValue(contact.properties.grade_applying_for)}</p>
                            )}
                            {getPropertyValue(contact.properties?.hs_object_id) && (
                              <p><strong>HubSpot ID:</strong> {getPropertyValue(contact.properties.hs_object_id)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '40px auto',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    color: '#1d1d1f',
    margin: 0,
    fontSize: '2.5rem',
    fontWeight: 600,
  },
  subtitle: {
    color: '#86868b',
    margin: '10px 0 0 0',
    fontSize: '1.1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#86868b',
    fontSize: '1.2rem',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '20px',
    borderRadius: '8px',
    margin: '20px 0',
    borderLeft: '4px solid #c62828',
  },
  success: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
    padding: '20px',
    borderRadius: '8px',
    margin: '20px 0',
    borderLeft: '4px solid #2e7d32',
  },
  listsContainer: {
    marginTop: '30px',
  },
  stats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 600,
    color: '#007bff',
  },
  statLabel: {
    color: '#86868b',
    fontSize: '0.9rem',
    marginTop: '5px',
  },
  listsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  listCard: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '20px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  listCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: '#007bff',
  },
  listTitle: {
    margin: '0 0 10px 0',
    color: '#1d1d1f',
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  listInfo: {
    margin: '5px 0',
    color: '#86868b',
    fontSize: '0.9rem',
  },
  listId: {
    fontFamily: "'Courier New', monospace",
    backgroundColor: '#e9ecef',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.8rem',
  },
  noLists: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    color: '#86868b',
  },
  refreshBtn: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#1d1d1f',
  },
  modalActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  dropdownContainer: {
    position: 'relative',
  },
  actionsButton: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 1001,
    minWidth: '150px',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '10px 15px',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'background-color 0.3s ease',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#86868b',
    padding: '0',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background-color 0.3s ease',
  },
  modalBody: {
    padding: '20px',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  contactsStats: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '4px solid #007bff',
  },
  contactsList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '15px',
  },
  contactCard: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '15px',
    transition: 'all 0.3s ease',
  },
  contactCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: '#007bff',
  },
  contactName: {
    margin: '0 0 10px 0',
    color: '#1d1d1f',
    fontSize: '1.1rem',
    fontWeight: 600,
  },
  contactDetails: {
    fontSize: '0.9rem',
    color: '#666',
    '& p': {
      margin: '5px 0',
    },
  },
  noContacts: {
    textAlign: 'center',
    color: '#86868b',
    fontSize: '1.1rem',
    padding: '40px',
  },
};

export default HubSpotLists;
